import moment from 'moment';
import HelperMixin from '../../Shared/HelperMixin.vue';
import PaginationHelper from '../../Shared/PaginationHelper.vue';
import { buildVoucherPrintHtml, openPrintWindow } from './printOfficialVoucher';

const TYPE_OPTIONS = [
    { value: 'issue', label: 'سند صرف' },
    { value: 'receipt', label: 'سند دخول' },
    { value: 'return', label: 'سند إرجاع' },
    { value: 'damage', label: 'سند إعدام' },
    { value: 'invoice', label: 'فاتورة نهائية' },
    { value: 'count', label: 'سند جرد' },
];

const TYPE_BADGE_CLASS = {
    issue: 'vouchers-type-badge is-issue',
    receipt: 'vouchers-type-badge is-receipt',
    return: 'vouchers-type-badge is-return',
    damage: 'vouchers-type-badge is-damage',
    invoice: 'vouchers-type-badge is-invoice',
    count: 'vouchers-type-badge is-count',
};

export default {
    name: 'OfficialVouchers',
    mixins: [HelperMixin],
    components: { PaginationHelper },

    async created() {
        await this.CheckLoginStatus();
        if (!this.canViewPage) {
            this.$helper.ShowMessage('error', 'غير مصرح', 'ليس لديك صلاحية للوصول إلى السندات الرسمية');
            this.href('/Home');
            return;
        }
        await this.loadBranches();
        await this.GetStatistics();
        this.GetInfo(1);
    },

    computed: {
        canViewPage() {
            if (!this.loginDetails) return false;
            if (this.hasInventoryAndStockAccessUi()) return true;
            if (this.isMinistryPharmacyDepartmentRole()) return true;
            if (this.isFacilityCenterRole() && this.loginDetails.branchesId) return true;
            return false;
        },
        statsCards() {
            const s = this.Statistics || {};
            return [
                this.statCard('إجمالي السندات', s.total, 'primary', 'ti ti-files'),
                this.statCard('سندات صرف', s.issue, 'info', 'ti ti-arrow-right'),
                this.statCard('سندات دخول', s.receipt, 'success', 'ti ti-arrow-left'),
                this.statCard('سندات جرد', s.count, 'warning', 'ti ti-clipboard-list'),
            ];
        },
        typeOptions() { return TYPE_OPTIONS; },
        typeBadgeClass() {
            return (typeKey) => TYPE_BADGE_CLASS[typeKey] || 'vouchers-type-badge';
        },
    },

    data() {
        return {
            ScreenTitle: 'السندات الرسمية',
            pageNo: 1,
            pageSize: 10,
            pages: 0,

            FilterType: '',
            FilterFacility: '',
            FilterFrom: '',
            FilterTo: '',
            Search: '',

            Info: [],
            Statistics: {},

            branchesList: [],

            currentDetail: null,
            detailLoading: false,
        };
    },

    filters: {
        moment(d) { return d ? moment(d).format('DD/MM/YYYY') : '—'; },
        momentTime(d) { return d ? moment(d).format('DD/MM/YYYY HH:mm') : '—'; },
        toNum(n) { return n == null ? 0 : Number(n).toLocaleString('en-US'); },
        toMoney(n) {
            if (n == null) return '—';
            const v = Number(n);
            if (!Number.isFinite(v)) return '—';
            return v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
    },

    methods: {
        tableRowNumber(i) {
            return ((Number(this.pageNo) - 1) * Number(this.pageSize)) + i + 1;
        },

        extractError(err) {
            if (err && err.response && err.response.data) {
                return typeof err.response.data === 'string'
                    ? err.response.data
                    : (err.response.data.message || 'حدث خطأ غير متوقع');
            }
            return (err && err.message) || 'حدث خطأ غير متوقع';
        },

        async loadBranches() {
            try {
                const res = await this.$http.GetAllBranches();
                this.branchesList = (res && res.data && res.data.info) || [];
            } catch {
                this.branchesList = [];
            }
        },

        async GetInfo(pageNo) {
            this.pageNo = pageNo || 1;
            this.$blockUI.Start();
            try {
                const res = await this.$http.GetOfficialVouchers({
                    pageNo: this.pageNo,
                    pageSize: this.pageSize,
                    Search: this.Search,
                    Type: this.FilterType,
                    BranchesId: this.FilterFacility,
                    From: this.FilterFrom,
                    To: this.FilterTo,
                });
                this.Info = (res && res.data && res.data.info) || [];
                const count = (res && res.data && res.data.count) || 0;
                this.pages = Math.ceil(count / this.pageSize);
            } catch (err) {
                this.Info = [];
                this.pages = 0;
                this.$helper.ShowMessage('error', 'تعذّر تحميل السندات', this.extractError(err));
            } finally {
                this.$blockUI.Stop();
            }
        },

        async GetStatistics() {
            try {
                const res = await this.$http.GetOfficialVouchersStatistics({
                    BranchesId: this.FilterFacility,
                    From: this.FilterFrom,
                    To: this.FilterTo,
                });
                this.Statistics = (res && res.data) || {};
            } catch {
                this.Statistics = {};
            }
        },

        ApplyFilters() {
            this.GetStatistics();
            this.GetInfo(1);
        },

        ResetFilters() {
            this.FilterType = '';
            this.FilterFacility = '';
            this.FilterFrom = '';
            this.FilterTo = '';
            this.Search = '';
            this.ApplyFilters();
        },

        async OpenDetail(item) {
            this.currentDetail = null;
            this.detailLoading = true;
            try {
                const res = await this.$http.GetOfficialVoucherOne(item.source, item.id);
                this.currentDetail = (res && res.data && res.data.info) || null;
            } catch (err) {
                this.$helper.ShowMessage('error', 'تعذّر تحميل التفاصيل', this.extractError(err));
            } finally {
                this.detailLoading = false;
            }
        },

        async PrintVoucher(item) {
            try {
                const res = await this.$http.GetOfficialVoucherOne(item.source, item.id);
                const data = (res && res.data && res.data.info) || null;
                if (!data) throw new Error('السند غير موجود');
                const html = buildVoucherPrintHtml(data, { mode: 'print' });
                openPrintWindow(html, false);
            } catch (err) {
                this.$helper.ShowMessage('error', 'تعذّر طباعة السند', this.extractError(err));
            }
        },

        async DownloadPdf(item) {
            try {
                const res = await this.$http.GetOfficialVoucherOne(item.source, item.id);
                const data = (res && res.data && res.data.info) || null;
                if (!data) throw new Error('السند غير موجود');
                const html = buildVoucherPrintHtml(data, { mode: 'pdf' });
                openPrintWindow(html, true);
            } catch (err) {
                this.$helper.ShowMessage('error', 'تعذّر تجهيز ملف PDF', this.extractError(err));
            }
        },

        PrintCurrentDetail() {
            if (!this.currentDetail) return;
            const html = buildVoucherPrintHtml(this.currentDetail, { mode: 'print' });
            openPrintWindow(html, false);
        },

        DownloadCurrentDetailPdf() {
            if (!this.currentDetail) return;
            const html = buildVoucherPrintHtml(this.currentDetail, { mode: 'pdf' });
            openPrintWindow(html, true);
        },
    },
};
